import { ChangeEvent, MouseEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@sternum/styled-system';
import { Menu } from '@sternum/ui';
import { Icon } from '@sternum/web/components';
import { useRedirectToDefaultPath, useSaveLastUsedClient, useSelectedClientId } from '@sternum/web/features/auth/hooks';
import { useClients } from '@sternum/web/features/client/hooks';
import { useClickAway, useDebounce } from '@sternum/web/hooks';
import { SpinnerIcon } from '@sternum/web/icons';
import { namespaces } from '@sternum/web/services/i18n';

import { NoResultsMessage, PopupInner, PopupStyled, SearchInput } from './WorkspacesSection.styles';

export function WorkspacesSearch() {
  const { t } = useTranslation(namespaces.appContainerWithMenu);
  const selectedClientId = useSelectedClientId();
  const { mutateAsync: saveLastUsedClient, isLoading: isSaveLastUsedClientInProgress } = useSaveLastUsedClient();
  const { redirectToDefaultPath } = useRedirectToDefaultPath();

  const [searchInput, setSearchInput] = useState('');
  const [isPopupOpen, setPopupOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useClickAway<HTMLInputElement>((e) => {
    const clickedInsideDropdown = popupRef.current?.contains(e.target as Element);

    if (!clickedInsideDropdown) {
      setPopupOpen(false);
    }
  });

  const debouncedSearchInput = useDebounce(searchInput);

  const { clients, isFetching: areClientsLoading } = useClients(
    { name: debouncedSearchInput || undefined },
    { enabled: isPopupOpen },
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPopupOpen(true);
  };

  const handleClearClick = (e: MouseEvent<SVGElement>) => {
    e.stopPropagation();
    setSearchInput('');
    setPopupOpen(false);
  };

  const isLoading = isSaveLastUsedClientInProgress || areClientsLoading;

  return (
    <Box>
      <SearchInput
        role="textbox"
        aria-label="workspaces search input"
        ref={inputRef}
        width="100%"
        iconHasPointerEvents
        placeholder={t('workspacesSearchPlaceholder')}
        leftIcon={<Icon type="SearchIcon" width={16} height={16} color={{ Dark: 'GreyDarker', Light: 'GreyDarker' }} />}
        rightIcon={
          searchInput && (
            <Icon
              type="XIcon"
              width={12}
              height={12}
              color={{ Dark: 'GreyDarker', Light: 'GreyDarker' }}
              hasCursorPointer
              onClick={handleClearClick}
            />
          )
        }
        value={searchInput}
        onChange={handleInputChange}
      />

      <PopupStyled
        role="presentation"
        aria-label="workspace options dropdown"
        ref={popupRef}
        trackedElementRef={inputRef}
        show={isPopupOpen}
        offsetY={20}
      >
        {isLoading && (
          <Box w="100%" px={12} py={8} textAlign="center">
            <SpinnerIcon width={12} height={12} />
          </Box>
        )}

        {!isLoading && clients.length === 0 && (
          <Box w="100%" px={12} py={8}>
            <NoResultsMessage>{t('noResultsMessage')}</NoResultsMessage>
          </Box>
        )}

        {!isLoading && (
          <PopupInner>
            {clients.map((client) => {
              const isSelected = client.id === selectedClientId;
              const clientId = client.id;

              return (
                <Menu.Item
                  key={client.id}
                  onClick={async () => {
                    if (isSelected) return;

                    await saveLastUsedClient({ clientId });

                    await redirectToDefaultPath(clientId);
                  }}
                >
                  {client.name}
                </Menu.Item>
              );
            })}
          </PopupInner>
        )}
      </PopupStyled>
    </Box>
  );
}
